import { createSelector } from 'reselect'
import { sortBy, groupBy, reverse } from 'lodash'

import { t } from 'plottr_locales'

import { robustlyParseStringDate } from '../helpers/date'
import { nonEmptyBackupFoldersSelector } from './backupsFirstOrder'
import { cloudFileListSelector } from './knownFilesFirstOrder'
import { isLoggedIntoProWithActiveLicenseSelector } from './secondOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').BackupFolder} BackupFolder
 * @typedef {import('../index').BackupsFoldersEntry} BackupsFoldersEntry
 * @typedef {import('../index').GroupedBackpuFoldersForDisplay} GroupedBackpuFoldersForDisplay
 * @typedef {import('../index').KnownFilesEntry} KnownFilesEntry
 */

/**
 * @param {Array<BackupFolder>} folders
 * @return {Array<BackupFolder>}
 */
const sortFolders = (folders) => {
  return sortBy(folders, (folder) => {
    return robustlyParseStringDate(folder.date)
  }).reverse()
}

/**
 * @param {BackupsFoldersEntry} fileObj
 * @return {String}
 */
const groupableName = (fileObj) => {
  const fileId = fileObj.fileId || fileObj.id
  const fileName =
    fileObj.name ||
    ('fileName' in fileObj && typeof fileObj.fileName === 'string' && fileObj.fileName) ||
    'Unnamed'
  if (fileObj.storagePath && fileId) {
    return fileId
  } else {
    return fileName.replace('(start-session)-', '')
  }
}

/**
 * @param {Date | String} dateObj
 * @param {boolean} makeShort
 */
const makeDateString = (dateObj, makeShort) => {
  let dateStr = ''
  try {
    const date = dateObj instanceof Date ? dateObj : robustlyParseStringDate(dateObj)
    const style = makeShort ? '{date, date, monthDay}' : '{date, date, medium}'
    dateStr = t(style, { date })
  } catch (error) {
    console.error('Error creating date string', error)
  }
  return dateStr
}

/**
 * @param {String} folderPath
 * @param {Array<KnownFilesEntry>} cloudFiles
 * @return {function(BackupsFoldersEntry): BackupsFoldersEntry}
 */
const addFileNameToCloudFile = (folderPath, cloudFiles) => (fileObject) => {
  if (fileObject.storagePath) {
    const fileId = fileObject.id || fileObject.fileId
    const proFile = cloudFiles.find(({ id }) => {
      return id === fileObject.id
    })
    return {
      ...fileObject,
      ...(proFile?.fileName || fileId ? { name: proFile?.fileName || 'Unnamed' } : {}),
    }
  } else {
    const name = fileObject.name.replace('(start-session)-', '').replace(/\.pltr$/, '')
    return {
      ...fileObject,
      name,
      localFilePathSegments: [folderPath, fileObject.name],
    }
  }
}

/**
 * @param {BackupsFoldersEntry} backup
 * @return {number}
 */
const backupDate = (backup) => {
  if (typeof backup.lastModified?.seconds === 'number') {
    if (typeof backup.lastModified?.nanoseconds === 'number') {
      return backup.lastModified.seconds * 1000 + backup.lastModified.nanoseconds / 1000
    } else {
      return backup.lastModified.seconds * 1000
    }
  } else if (typeof backup?.lastEdited === 'number') {
    return backup.lastEdited
  } else {
    return 0
  }
}

/**
 * @type {function(PlottrState): Array<GroupedBackpuFoldersForDisplay>}
 */
export const groupedSortedBackupFoldersSelector = createSelector(
  nonEmptyBackupFoldersSelector,
  cloudFileListSelector,
  isLoggedIntoProWithActiveLicenseSelector,
  (backupFolders, cloudFiles, isInProMode) => {
    const sortedFolders = sortFolders(backupFolders)
    return sortedFolders
      .map((f) => {
        const filtered = f.backups.filter((backup) => {
          return (
            (backup?.storagePath && typeof backup?.storagePath === 'string' && isInProMode) ||
            typeof backup?.storagePath === 'undefined'
          )
        })
        const groups = reverse(
          sortBy(
            groupBy(filtered.map(addFileNameToCloudFile(f.path, cloudFiles)), groupableName),
            (backups) => {
              return backups.reduce((acc, backup) => {
                return Math.max(acc, backupDate(backup))
              }, 0)
            }
          )
        )
        return {
          ...f,
          backups: filtered,
          groups,
          longDateStr: makeDateString(f.date, false),
          shortDateStr: makeDateString(f.date, true),
        }
      })
      .filter(({ backups }) => {
        return Array.isArray(backups) && backups.length > 0
      })
  }
)
